import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { setToken } from "@/lib/api";
import { applyTierTheme } from "@/lib/themes";
import BrandName from "@/components/BrandName";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Shield, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

import { getBackendUrl } from "../lib/backend-url";
const PUBLIC_BASE = `${getBackendUrl()}/api/v1`;

/** Public landing for `/invite/:token`.
 *  No auth header — the token IS the credential. Recipient sets a password,
 *  the backend creates their account + applies the project_override.
 */
export default function OverrideInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [invite, setInvite]     = useState(null);
  const [password, setPassword] = useState("");
  const [email, setEmail]       = useState("");
  const [name, setName]         = useState("");
  const [phone, setPhone]       = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${PUBLIC_BASE}/override-invites/public/${token}`);
        setInvite(data);
        setEmail(data.invitee_email || "");
        setName(data.invitee_name || "");
        setPhone(data.invitee_phone || "");
        applyTierTheme(data.role_tier);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Invite link could not be loaded");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${PUBLIC_BASE}/override-invites/public/${token}/accept`,
        { password, email: email || undefined, phone, full_name: name },
      );
      setToken(data.access_token);
      await refresh();
      toast.success(`Welcome — you're on "${data.project_title}"`);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not claim invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60 text-xs uppercase tracking-[0.3em]">
        Loading invite…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md cs-card rounded-2xl p-8 text-center" data-testid="invite-error">
          <AlertTriangle size={28} className="mx-auto text-red-400 mb-3" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Link Unavailable</h1>
          <p className="mt-3 text-sm text-white/65">{error}</p>
          <Link to="/" className="inline-block mt-6 text-xs uppercase tracking-[0.25em] text-[#F5B82E] hover:underline">
            Back to CALL · SHEET
          </Link>
        </div>
      </div>
    );
  }

  const assoc = invite.temporary_association;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="invite-page">
      <div className="w-full max-w-xl">
        <Link to="/" className="block mb-8 text-center">
          <BrandName variant="gold" size="lg" withClapper />
        </Link>

        <div className="cs-card rounded-2xl p-8 cs-grain relative">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} style={{ color: "var(--association-theme)" }} />
            <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "var(--association-theme)" }}>
              Production Invite · Temporary Association
            </span>
          </div>

          <h1 className="font-display text-3xl uppercase tracking-tight" data-testid="invite-project-title">
            {invite.project_title}
          </h1>
          <p className="mt-2 text-sm text-white/65">
            <span className="font-semibold text-white">{invite.production_house}</span> is bringing you onboard
            as <span className="font-semibold text-white">{invite.role_label}</span>.
          </p>

          {/* Co-branded badge for the temporary association */}
          <div
            className="mt-5 inline-flex items-center gap-3 px-4 py-2.5 rounded-full"
            style={{
              background: `${assoc.color}1F`,
              border: `1px solid ${assoc.color}55`,
            }}
            data-testid="invite-assoc-badge"
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center font-display text-[10px] font-bold text-white"
              style={{ background: assoc.color }}
            >
              {assoc.short_name?.slice(0, 2)}
            </span>
            <div className="leading-tight">
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">Temporary Affiliation</div>
              <div className="text-sm font-display font-semibold text-white">{assoc.short_name}</div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-[11px] text-white/60">
            <Meta label="Starts"  value={fmtDate(invite.starts_at)} />
            <Meta label="Expires" value={fmtDate(invite.expires_at)} />
            {invite.daily_rate > 0 && (
              <Meta label="Daily Rate" value={`₹${Number(invite.daily_rate).toLocaleString()}`} />
            )}
            <Meta label="Status"  value={invite.status} highlight />
          </div>

          <form onSubmit={accept} className="mt-8 space-y-4" data-testid="invite-claim-form">
            <Pair label="Full name" tid="invite-claim-name-field">
              <Input
                value={name} onChange={(e) => setName(e.target.value)}
                required data-testid="invite-claim-name"
                className="mt-2 h-11 bg-black/40 border-white/15"
              />
            </Pair>

            <Pair label="Email" tid="invite-claim-email-field">
              <Input
                type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                required readOnly={!!invite.invitee_email}
                data-testid="invite-claim-email"
                className="mt-2 h-11 bg-black/40 border-white/15"
              />
              {invite.invitee_email && (
                <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/40">
                  Locked to invited address
                </div>
              )}
            </Pair>

            <Pair label="Phone (optional)" tid="invite-claim-phone-field">
              <Input
                value={phone} onChange={(e) => setPhone(e.target.value)}
                data-testid="invite-claim-phone"
                className="mt-2 h-11 bg-black/40 border-white/15"
              />
            </Pair>

            <Pair label="Set a password" tid="invite-claim-password-field">
              <Input
                type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                required minLength={6}
                placeholder="Min 6 characters"
                data-testid="invite-claim-password"
                className="mt-2 h-11 bg-black/40 border-white/15"
              />
            </Pair>

            <Button
              type="submit" disabled={submitting}
              className="btn-themed h-12 w-full rounded-full text-xs uppercase tracking-[0.25em] font-semibold"
              data-testid="invite-claim-submit"
            >
              {submitting ? "Claiming…" : "Claim Slot & Enter Project →"}
            </Button>
          </form>

          <div className="mt-6 flex items-start gap-2 text-[11px] text-white/45">
            <CheckCircle size={12} className="mt-0.5 shrink-0 text-emerald-400/80" />
            <p>
              You&apos;re being temporarily affiliated with <strong>{assoc.short_name}</strong> for this project only.
              You can keep your permanent association — edit it from <em>Settings</em> after sign-in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pair({ label, children, tid }) {
  return (
    <div data-testid={tid}>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      {children}
    </div>
  );
}

function Meta({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.25em] uppercase text-white/40">{label}</div>
      <div className={`mt-0.5 ${highlight ? "uppercase tracking-[0.18em] text-amber-300" : "text-white"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}
